// PerformanceInventoryDO RPC contract (docs/inventory.md#durable-object).
// The API Worker is the only caller; browsers never reach the DO directly.
import { z } from 'zod';
import { InventoryOperationType, InventoryState, SalesChannel } from './enums';
import { BreAuthSub, Id, IdempotencyKey, Timestamp } from './primitives';

const HoldContext = z.object({
  performance_id: Id,
  cart_id: Id,
  channel: SalesChannel,
  customer_sub: BreAuthSub.nullable(),
  staff_session_id: Id.nullable(),
  idempotency_key: IdempotencyKey,
});

/** Atomically hold every listed seat for the cart, or none of them. */
export const ReserveSeatsRequest = HoldContext.extend({
  unit_ids: z.array(Id).min(1).max(50),
  hold_seconds: z.int().min(60).max(1_800),
  /** Box office may cart seats out of a hold or restricted pool with permission. */
  allow_from_states: z.array(InventoryState).default(['AVAILABLE']),
});
export type ReserveSeatsRequest = z.infer<typeof ReserveSeatsRequest>;

/** Hold N fungible GA units in one area. */
export const ReserveGeneralAdmissionRequest = HoldContext.extend({
  ga_area_id: Id,
  quantity: z.int().min(1).max(50),
  hold_seconds: z.int().min(60).max(1_800),
});
export type ReserveGeneralAdmissionRequest = z.infer<typeof ReserveGeneralAdmissionRequest>;

/**
 * Hold N admissions in one timed-entry slot (D-86).
 *
 * The same shape as a GA hold, because that is what it is: the slot is a
 * time-bounded subdivision of a GA area, so the units, the states, the
 * expiry and the commit path are all the ordinary ones.
 */
export const ReserveTimedEntryRequest = HoldContext.extend({
  timed_entry_slot_id: Id,
  quantity: z.int().min(1).max(50),
  hold_seconds: z.int().min(60).max(1_800),
});
export type ReserveTimedEntryRequest = z.infer<typeof ReserveTimedEntryRequest>;

export const HoldRejectionCode = z.enum([
  'UNIT_UNAVAILABLE',
  /** Part of a table that is sold whole (D-152): the ids are the seats also needed. */
  'TABLE_SOLD_WHOLE',
  'PERFORMANCE_NOT_SELLABLE',
  'CART_LIMIT_EXCEEDED',
  'INSUFFICIENT_GA_CAPACITY',
  'INSUFFICIENT_SLOT_CAPACITY',
  'CART_EXPIRED',
  'IDEMPOTENCY_KEY_REUSED',
]);

export const ReserveResult = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    cart_id: Id,
    unit_ids: z.array(Id),
    expires_at: Timestamp,
  }),
  z.object({
    ok: z.literal(false),
    code: HoldRejectionCode,
    unavailable_unit_ids: z.array(Id),
  }),
]);
export type ReserveResult = z.infer<typeof ReserveResult>;

/** Hold the best `quantity` seats together, chosen and held in one transaction. */
export const BestAvailableRequest = HoldContext.extend({
  quantity: z.int().min(1).max(50),
  hold_seconds: z.int().min(60).max(1_800),
  section_ids: z.array(Id).max(50).optional(),
  price_zone_ids: z.array(Id).max(50).optional(),
  allow_split: z.boolean().default(false),
  allow_from_states: z.array(InventoryState).default(['AVAILABLE']),
});
export type BestAvailableRequest = z.infer<typeof BestAvailableRequest>;

export const BestAvailableResult = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), cart_id: Id, unit_ids: z.array(Id), expires_at: Timestamp, together: z.boolean() }),
  z.object({ ok: z.literal(false), code: HoldRejectionCode, unavailable_unit_ids: z.array(Id) }),
]);
export type BestAvailableResult = z.infer<typeof BestAvailableResult>;

export const ReleaseCartRequest = z.object({ performance_id: Id, cart_id: Id, unit_ids: z.array(Id).optional() });
export const ExtendCartRequest = z.object({ performance_id: Id, cart_id: Id, hold_seconds: z.int().min(60).max(1_800) });

/**
 * A single-phase durable change: holds, kills, pools, zone remaps, releases.
 * The DO locks, writes one D1 batch (CAS + COMMITTED operation + history via
 * trigger) and applies locally. Units in a live cart are refused unless the
 * caller may preempt.
 */
export const ApplyDurableChangeRequest = z.object({
  performance_id: Id,
  operation: z.object({
    operation_id: Id,
    operation_type: InventoryOperationType,
    idempotency_key: IdempotencyKey,
    actor_type: z.enum(['STAFF', 'SERVICE', 'SYSTEM']),
    actor_id: Id.nullable(),
    reason: z.string().max(500).nullable(),
    reason_code: z.string().max(40).nullable(),
    hold_id: Id.nullable(),
    refund_id: Id.nullable().optional(),
    exchange_id: Id.nullable().optional(),
  }),
  changes: z
    .array(
      z.object({
        unit_id: Id,
        to_state: InventoryState,
        to_hold_id: Id.nullable(),
        to_price_zone_id: Id.nullable().optional(),
      }),
    )
    .min(1)
    .max(5000),
  preempt_carts: z.boolean().default(false),
});
export type ApplyDurableChangeRequest = z.infer<typeof ApplyDurableChangeRequest>;

export const DurableChangeRejectionCode = z.enum(['UNIT_UNAVAILABLE', 'UNIT_IN_CART', 'INVALID_TRANSITION', 'UNIT_NOT_FOUND', 'IDEMPOTENCY_KEY_REUSED']);

export const ApplyDurableChangeResult = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), operation_id: Id, units: z.int().min(0), preempted_cart_ids: z.array(Id) }),
  z.object({ ok: z.literal(false), code: DurableChangeRejectionCode, unit_ids: z.array(Id) }),
]);
export type ApplyDurableChangeResult = z.infer<typeof ApplyDurableChangeResult>;

/** Everything the admin inventory screen needs, including what the public snapshot hides. */
export const UnitSnapshot = z.object({
  unit_id: Id,
  state: InventoryState,
  version: z.int().min(1),
  hold_id: Id.nullable(),
  section_id: Id.nullable(),
  row_id: Id.nullable(),
  ga_area_id: Id.nullable(),
  price_zone_id: Id.nullable(),
  cart_held: z.boolean(),
  locked: z.boolean(),
});
export type UnitSnapshot = z.infer<typeof UnitSnapshot>;

/**
 * Phase 1 of an order commit. The DO locks the cart's units, writes
 * PAYMENT_PENDING + the PREPARED inventory_operation to D1, and returns the
 * versions the commit batch must CAS against.
 */
export const PrepareCommitRequest = z.object({
  performance_id: Id,
  cart_id: Id,
  order_id: Id,
  operation_id: Id,
  unit_ids: z.array(Id).min(1),
  decide_by: Timestamp,
  idempotency_key: IdempotencyKey,
});

export const PrepareCommitResult = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    operation_id: Id,
    units: z.array(
      z.object({
        unit_id: Id,
        version: z.int().min(1),
        origin_state: InventoryState,
        origin_hold_id: Id.nullable(),
      }),
    ),
  }),
  z.object({ ok: z.literal(false), code: HoldRejectionCode, unavailable_unit_ids: z.array(Id) }),
]);

/** Phase 2: tell the DO what D1 decided. D1 is authoritative if this is lost. */
export const FinalizeOperationRequest = z.object({
  performance_id: Id,
  operation_id: Id,
  outcome: z.enum(['COMMITTED', 'ABORTED']),
});

/** Public availability. Deliberately hides hold classes and customer identity. */
export const PublicUnitAvailability = z.enum(['AVAILABLE', 'HELD_BY_YOU', 'UNAVAILABLE', 'ACCESSIBLE_ON_REQUEST']);

export const AvailabilitySnapshot = z.object({
  performance_id: Id,
  inventory_version: z.int().min(0),
  generated_at: Timestamp,
  units: z.array(z.object({ unit_id: Id, availability: PublicUnitAvailability })),
  ga_areas: z.array(z.object({ ga_area_id: Id, available_quantity: z.int().min(0) })),
});
export type AvailabilitySnapshot = z.infer<typeof AvailabilitySnapshot>;
